"use client";

import { TRACKING } from "@/lib/constants/tracking";
import Link from "next/link";
import { useEffect, useState } from "react";
import ImageWrapper from "./ui/ImageWrapper";

type NewsVideoCardProps = {
  title: string;
  imageUrl: string;
  alt: string;
  duration: string;
  videoUrl: string;
  isPlaying?: boolean;
  onPlay?: () => void;
  excerpt?: string;
  displayDate?: string;
  isMobile?: boolean;
};

const NewsVideoCard: React.FC<NewsVideoCardProps> = ({
  title,
  imageUrl,
  alt,
  duration,
  videoUrl,
  isPlaying = false,
  onPlay,
  excerpt = "",
  displayDate = new Date().toISOString(),
  isMobile = false,
}) => {
  const [play, setPlay] = useState(false);

  // When another video starts playing, stop this one
  useEffect(() => {
    if (!isPlaying && play) {
      setPlay(false);
    }
  }, [isPlaying, play]);

  // Convert YouTube URL to embed format
  const getEmbedUrl = (url: string) => {
    let videoId = "";

    if (url.includes("shorts")) {
      videoId = url.split("shorts/")[1]?.split("?")[0];
    } else if (url.includes("watch?v=")) {
      videoId = url.split("v=")[1]?.split("&")[0];
    } else if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1]?.split("?")[0];
    }

    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&rel=0&controls=1`;
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden ${TRACKING.VIDEO_PLAYER.LONG_VIDEO_CARD}`}>
      {/* Video Thumbnail / Iframe */}
      <div
        className={`aspect-video relative cursor-pointer bg-black ${TRACKING.VIDEO_PLAYER.LONG_VIDEO_THUMBNAIL}`}
        onClick={() => {
          setPlay(true);
          onPlay?.();
        }}
        title={alt}
      >
        {!play ? (
          <>
            {/* Thumbnail Image */}
            <ImageWrapper
              src={imageUrl}
              alt={alt}
              fill
              sizes="(max-width: 768px) 100vw, 33vw"
              sourceWidth={isMobile ? 350 : 400}
              sourceHeight={isMobile ? 197 : 225}
              className="object-cover"
              title={alt}
            />

            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-black/30" />

            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex items-center justify-center w-[46px] h-[46px] rounded-full bg-red-600 hover:bg-red-700 transition-colors shadow-lg">
                <svg viewBox="0 0 24 24" fill="white" className="w-[30px] h-[30px]">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </>
        ) : (
          <iframe
            width="100%"
            height="100%"
            src={getEmbedUrl(videoUrl)}
            title={title}
            className="w-full h-full"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        )}
      </div>

      {/* Video Title */}
      <div className="p-4">
        <p className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 transition-colors duration-300 min-h-[56px] md:min-h-[56px]">
          {title}
        </p>

        <p className="text-sm text-gray-600 mb-3 line-clamp-3">{excerpt}</p>

        {/* Meta Info */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-2">
            <span>
              Published:{" "}
              {new Date(displayDate).toLocaleDateString("en-IN", {
                year: "numeric",
                month: "long",
                day: "numeric",
                timeZone: "Asia/Kolkata",
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsVideoCard;
